import React, { useState, useEffect } from 'react';
import { Button } from '@/components/ui/button';
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from '@/components/ui/card';
import { Textarea } from '@/components/ui/textarea';
import { Input } from '@/components/ui/input';
import { Label } from '@/components/ui/label';
import { Tabs, TabsContent, TabsList, TabsTrigger } from '@/components/ui/tabs';
import { toast } from 'sonner';
import { supabase } from '@/integrations/supabase/client';
import { AlertDialog, AlertDialogAction, AlertDialogCancel, AlertDialogContent, AlertDialogDescription, AlertDialogFooter, AlertDialogHeader, AlertDialogTitle, AlertDialogTrigger } from '@/components/ui/alert-dialog';
import { RefreshCw, RotateCcw, History, Clock } from 'lucide-react';
import SystemMessageHistory from './SystemMessageHistory';

const DEFAULT_SYSTEM_MESSAGE = `You are a data analyst reviewing data from an ecommerce seller's company. You will be asked questions by someone performing due diligence to determine suitability for lending.

CRITICAL RESPONSE REQUIREMENTS:
- You MUST provide complete analysis with actual results and findings in your FIRST response
- NEVER end responses with phrases like "Let me proceed", "Give me a moment", "I will now...", or similar incomplete statements
- If database queries encounter errors, the system will automatically retry with adapted queries
- You will receive final results after all retry attempts - analyze and present these immediately
- ALWAYS conclude with results based on successfully retrieved data
- If all queries fail, provide analysis recommendations based on what data sources are available
- Some columns are stored as character varying but contain numeric values. When performing numeric operations (e.g. sorting, comparisons, aggregations), these columns must be cast using column_name::numeric.
- Always exclude NULL values when retrieving numeric maximums, minimums, or performing calculations. Use WHERE column_name IS NOT NULL

You have access to the following tools through the MCP server:
1. "search" - Execute SQL queries to analyze organization databases to search and analyze data
2. "fetch" - Fetch data from tables with optional filters

Data Connections:
The organisation may have multiple connections, including:
- Shopify: ecommerce order, product, customer data  
- Amazon Selling Partner: marketplace order, item, customer data  
- Walmart Marketplace: similar marketplace data  
- Google Sheets: financial statements, business reports  
- CSV Imports: uploaded ad-hoc or external data  

**Amazon CSV Import Schema**
For Amazon accounts using CSV transaction imports (connection label contains 'amazon_csv' or import_type='ecommerce' with ecommerce_subtype='amazon'), use the 'amazon_csv_data' table instead of API tables.

Table: public.amazon_csv_data
Columns:
- sku: Product SKU identifier
- type: Transaction type (see values below)
- date_time: Transaction timestamp
- quantity: Number of units
- product_sales: Product sale amount (VARCHAR, may contain commas)
- shipping_credits: Shipping credit amount
- gift_wrap_credits: Gift wrap credit amount
- promotional_rebates: Promotional discount amount
- total: Net transaction amount (VARCHAR, may contain commas)

Type values for filtering:
- 'Order': Regular sales orders
- 'Refund': Customer refunds
- 'Chargeback Refund': Chargeback-related refunds
- 'Service Fee': Amazon platform fees
- 'FBA Customer Return Fee': FBA return processing fees
- 'Shipping Services': Shipping-related charges
- 'Adjustment': Manual adjustments
- 'Fee Adjustment': Fee corrections
- 'FBA Inventory Fee': Storage and inventory fees
- 'Transfer': Payout transfers
- 'Amazon Fees': General Amazon fees
- 'Debt': Outstanding debt entries
- 'Order_Retrocharge': Retroactive order charges
- 'Refund_Retrocharge': Retroactive refund adjustments

Amazon CSV Metric Calculations:
- GMV: SUM(product_sales + shipping_credits + gift_wrap_credits) WHERE type = 'Order'
- NMV: SUM(total) WHERE type IN ('Order', 'Refund', 'Refund_Retrocharge')
- Units Sold: SUM(quantity) WHERE type = 'Order'
- Refunds: SUM(total) WHERE type IN ('Refund', 'Chargeback Refund')

Important: Numeric columns (product_sales, shipping_credits, total, etc.) are stored as VARCHAR and may contain commas. Always use: CAST(REPLACE(column_name, ',', '') AS numeric) for calculations.

**Amazon CSV Inventory Data (Separate Source)**
For Amazon CSV accounts, inventory/stock data is stored separately from transactions:

Inventory Table: org_{org_id}_inventory_snapshots (in separate snapshots database)
Columns:
- sku: Product SKU identifier (TEXT)
- source: Data source identifier (TEXT)
- connection_id: Connection identifier (TEXT)
- quantity: Current stock quantity (INTEGER)
- snapshot_date: Date of the inventory snapshot (DATE)
- last_updated_at: When the record was last updated (TIMESTAMP)

Note: Inventory snapshots are stored in a separate database and are fetched automatically when generating SKU reports. 
The inventory data is used to calculate:
- Stock Held: quantity from the most recent snapshot per SKU
- GEV (Gross Equity Value): stock_held × avg_gmv (limited to sellable units)
- NEV (Net Equity Value): stock_held × avg_nmv (limited to sellable units)
- Days Until Stockout: (stock_held × days_in_period) / units_sold
- NERV (Net Effective Realizable Value): NEV × (1 - DR SKU %)

When querying Stock Held for Amazon CSV accounts, note that inventory data may not be available if inventory snapshots are not enabled for the organization.

Only use connections listed in the connection context.
If only Shopify is available, do not suggest or query Amazon, Walmart, or others.  
Assume you work with all available connections.

Mathematical Formatting Guidelines:
- When presenting mathematical calculations, formulas, or percentages, use proper LaTeX formatting with $$ delimiters for display math.
- For display formulas (centered, on their own line), use: $$formula$$
- For inline math (within text), use: $formula$
- Example display formula: $$\\text{Repeat Customer Rate} = \\left( \\frac{\\text{Number of Repeat Customers}}{\\text{Total Number of Customers}} \\right) \\times 100$$
- Example inline formula: The rate is $\\frac{1}{3} \\times 100 = 33.33\\%$
- Always use double backslashes for LaTeX commands in formulas (e.g., \\frac, \\text, \\left, \\right)

ERROR HANDLING INSTRUCTIONS:
- The system automatically retries failed queries with progressively simpler approaches
- You will receive the final result after all retry attempts
- Focus on providing results from successfully retrieved data
- If queries fail completely, recommend alternative analysis approaches
- NEVER ask the user to re-ask the question - provide complete analysis immediately

Context Variables  
Connection Context: Available connections will be provided
Organization: {organization_name}

Relevant Schema Context (based on query similarity):
\${schemaContext}`;

const SystemMessageEditor = () => {
  const [systemMessage, setSystemMessage] = useState('');
  const [loading, setLoading] = useState(true);
  const [saving, setSaving] = useState(false);
  const [hasChanges, setHasChanges] = useState(false);
  const [changeReason, setChangeReason] = useState('');
  const [currentVersion, setCurrentVersion] = useState<number>(1);
  const [lastChanged, setLastChanged] = useState<string>('');

  useEffect(() => {
    loadSystemMessage();
  }, []);

  const loadSystemMessage = async () => {
    setLoading(true);
    try {
      // Load current active message from history
      const { data: historyData, error: historyError } = await supabase
        .from('system_message_history')
        .select('message_content, version_number, created_at, changed_by_email')
        .eq('is_active', true)
        .single();

      if (historyError && historyError.code !== 'PGRST116') {
        // Fallback to app_settings if no history exists
        const { data, error } = await supabase
          .from('app_settings')
          .select('value')
          .eq('key', 'mcp_system_message')
          .single();

        if (error && error.code !== 'PGRST116') {
          throw error;
        }

        const message = data?.value || DEFAULT_SYSTEM_MESSAGE;
        setSystemMessage(message);
        setCurrentVersion(1);
        setLastChanged('');
      } else {
        setSystemMessage(historyData.message_content);
        setCurrentVersion(historyData.version_number);
        setLastChanged(`v${historyData.version_number} by ${historyData.changed_by_email} on ${new Date(historyData.created_at).toLocaleDateString()}`);
      }

      setHasChanges(false);
    } catch (error) {
      console.error('Error loading system message:', error);
      toast.error('Failed to load system message');
      setSystemMessage(DEFAULT_SYSTEM_MESSAGE);
    } finally {
      setLoading(false);
    }
  };

  const handleMessageChange = (value: string) => {
    setSystemMessage(value);
    setHasChanges(true);
  };

  const handleSave = async () => {
    setSaving(true);
    try {
      const { data: { user } } = await supabase.auth.getUser();

      if (!user) {
        throw new Error('User not authenticated');
      }

      // Get current max version number
      const { data: maxVersionData, error: maxVersionError } = await supabase
        .from('system_message_history')
        .select('version_number')
        .order('version_number', { ascending: false })
        .limit(1)
        .single();

      if (maxVersionError && maxVersionError.code !== 'PGRST116') {
        throw maxVersionError;
      }

      const nextVersion = (maxVersionData?.version_number || 0) + 1;

      // Mark all versions as inactive
      const { error: deactivateError } = await supabase
        .from('system_message_history')
        .update({ is_active: false })
        .eq('is_active', true);

      if (deactivateError) throw deactivateError;

      // Insert new version
      const { error: insertError } = await supabase
        .from('system_message_history')
        .insert({
          message_content: systemMessage,
          changed_by_user_id: user.id,
          changed_by_email: user.email || '',
          change_reason: changeReason || null,
          is_active: true,
          character_count: systemMessage.length,
          version_number: nextVersion
        });

      if (insertError) throw insertError;

      // Update app_settings for backward compatibility
      const { error: updateError } = await supabase
        .from('app_settings')
        .upsert({
          key: 'mcp_system_message',
          value: systemMessage,
          description: 'System message used by the MCP chat assistant for data analysis and due diligence reviews',
          updated_at: new Date().toISOString()
        }, {
          onConflict: 'key'
        });

      if (updateError) throw updateError;

      setHasChanges(false);
      setChangeReason('');
      setCurrentVersion(nextVersion);
      setLastChanged(`v${nextVersion} by ${user.email} on ${new Date().toLocaleDateString()}`);
      toast.success('System message updated');
    } catch (error) {
      console.error('Error saving system message:', error);
      toast.error('Failed to save system message');
    } finally {
      setSaving(false);
    }
  };

  const handleResetToDefault = () => {
    setSystemMessage(DEFAULT_SYSTEM_MESSAGE);
    setHasChanges(true);
  };

  const handleMessageReverted = (content: string) => {
    setSystemMessage(content);
    setHasChanges(false);
    loadSystemMessage(); // Refresh version info
  };

  const characterCount = systemMessage.length;

  if (loading) {
    return (
      <Card>
        <CardContent className="flex items-center justify-center py-8">
          <RefreshCw className="h-6 w-6 animate-spin mr-2" />
          Loading system message...
        </CardContent>
      </Card>
    );
  }

  return (
    <Tabs defaultValue="editor" className="space-y-4">
      <TabsList>
        <TabsTrigger value="editor" className="flex items-center gap-2">
          <RefreshCw className="h-4 w-4" />
          Message Editor
        </TabsTrigger>
        <TabsTrigger value="history" className="flex items-center gap-2">
          <History className="h-4 w-4" />
          Version History
        </TabsTrigger>
      </TabsList>

      <TabsContent value="editor" className="space-y-4">
        <Card>
          <CardHeader>
            <CardTitle className="flex items-center justify-between">
              <span>MCP Chat System Message</span>
              {lastChanged && (
                <div className="flex items-center gap-2 text-sm text-muted-foreground">
                  <Clock className="h-4 w-4" />
                  {lastChanged}
                </div>
              )}
            </CardTitle>
            <CardDescription>
              Configure the system message that guides the AI assistant's behavior for data analysis and due diligence reviews.
              This message is sent with every chat request to provide context and instructions.
            </CardDescription>
          </CardHeader>
          <CardContent className="space-y-4">
            <div className="space-y-2">
              <div className="flex justify-between items-center">
                <label className="text-sm font-medium">System Message</label>
                <span className="text-sm text-muted-foreground">
                  {characterCount.toLocaleString()} characters
                </span>
              </div>
              <Textarea
                data-testid="message-editor"
                value={systemMessage}
                onChange={(e) => handleMessageChange(e.target.value)}
                placeholder="Enter the system message for the MCP chat assistant..."
                className="min-h-[400px] font-mono text-sm"
              />
              <p className="text-xs text-muted-foreground">
                This message will be automatically enhanced with dynamic connection context and schema information.
                Use {'{organization_name}'} as a placeholder for the organization name.
              </p>
            </div>

            <div className="space-y-2">
              <Label htmlFor="change-reason">Change Reason (Optional)</Label>
              <Input
                id="change-reason"
                value={changeReason}
                onChange={(e) => setChangeReason(e.target.value)}
                placeholder="Brief description of why this change was made..."
                className="text-sm"
              />
              <p className="text-xs text-muted-foreground">
                Adding a reason helps track the purpose of each change in the version history.
              </p>
            </div>

            <div className="flex justify-between gap-3">
              {/* <AlertDialog>
                <AlertDialogTrigger asChild>
                  <Button variant="outline" className="flex items-center gap-2">
                    <RotateCcw className="h-4 w-4" />
                    Reset to Default
                  </Button>
                </AlertDialogTrigger>
                <AlertDialogContent>
                  <AlertDialogHeader>
                    <AlertDialogTitle>Reset to Default Message?</AlertDialogTitle>
                    <AlertDialogDescription>
                      This will replace the current system message with the default template. 
                      Any custom changes will be lost. You can save after reviewing the default message.
                    </AlertDialogDescription>
                  </AlertDialogHeader>
                  <AlertDialogFooter>
                    <AlertDialogCancel>Cancel</AlertDialogCancel>
                    <AlertDialogAction onClick={handleResetToDefault}>
                      Reset to Default
                    </AlertDialogAction>
                  </AlertDialogFooter>
                </AlertDialogContent>
              </AlertDialog> */}

              <Button
                data-testid="save-message"
                onClick={handleSave}
                disabled={!hasChanges || saving}
                className="flex items-center gap-2"
              >
                {saving ? (
                  <>
                    <RefreshCw className="h-4 w-4 animate-spin" />
                    Saving...
                  </>
                ) : (
                  'Save Changes'
                )}
              </Button>
            </div>
          </CardContent>
        </Card>
      </TabsContent>

      <TabsContent value="history" className="space-y-4">
        <SystemMessageHistory onMessageReverted={handleMessageReverted} />
      </TabsContent>
    </Tabs>
  );
};

export default SystemMessageEditor;